"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import Link from "next/link";
import { REFERRAL_CONTENT } from "@/constants/content";
import { GTMEvent, trackEvent } from "@/lib/utils/gtm-events";
import { fetchRewardProgramData } from "@/lib/rewardProgramCache";
import { useAppData } from "@/store/appStore";

function ReferralCard({
	onClose,
	templateMessage,
}: {
	onClose: () => void;
	templateMessage: string;
}) {
	const stripHtml = (str: string) => str.replace(/<[^>]*>/g, "");
	const parts = templateMessage.split("<br>");
	const firstPart = stripHtml(parts[0] || "");
	const secondPart = stripHtml(parts[1] || "");
	const thirdPart = stripHtml(parts[2] || "");

	return (
		<div className="relative rounded-3xl p-[1.5px] bg-gradient-to-b from-automint-neon to-transparent">
			<div className="bg-automint-black rounded-3xl p-5 w-[228px] min-h-[245px] text-center flex flex-col items-center justify-center overflow-hidden">
				{/* Close button */}
				<button
					onClick={onClose}
					className="absolute -top-3 -right-2 p-1"
					aria-label="Close card"
				>
					<Image
						src="/new_icons/general/close.svg"
						alt="Close"
						width={30}
						height={30}
					/>
				</button>

				<h3 className="leading-none text-white text-lg font-semibold line-clamp-2">
					{firstPart}
				</h3>
				<p className="-mt-1 text-white text-lg mb-1 font-light line-clamp-2">
					{secondPart}
				</p>
				<p className="text-[12px] text-white/50 uppercase tracking-wide mb-4 font-medium line-clamp-2">
					{thirdPart}
				</p>

				<div className="flex flex-col gap-2 w-full py-2">
					<Link
						href={REFERRAL_CONTENT.links.primary}
						onClick={() =>
							trackEvent(GTMEvent.REFERRAL_PAGE_VISITED, {
								origin: "rewards_popup",
							})
						}
						className="inline-flex items-center justify-center bg-automint-neon text-automint-black text-sm  font-normal px-4 py-2.5 rounded-[14px]  transition-colors"
					>
						{REFERRAL_CONTENT.primaryButton}
					</Link>
					<Link
						href="/profile?showTerms=true#referrals"
						onClick={() => {
							trackEvent(GTMEvent.REFERRAL_TNC_VISITED, {
								origin: "rewards_popup",
							});
							onClose();
						}}
						className="text-automint-neon text-sm font-light text-center underline py-2"
					>
						{REFERRAL_CONTENT.secondaryButton}
					</Link>
				</div>
			</div>
		</div>
	);
}

export default function RewardsSection({
	embedded = false,
	isAuthorized = false,
	disableAutoOpen = false,
	onReady,
}: {
	embedded?: boolean;
	isAuthorized?: boolean;
	disableAutoOpen?: boolean;
	onReady?: () => void;
}) {
	const [appData, setData] = useAppData();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const rewardData = appData?.rewardProgramData ?? null;

	useEffect(() => {
		if (!isAuthorized) {
			if (appData?.rewardProgramData !== null) {
				setData({ rewardProgramData: null });
			}
			onReady?.();

			return;
		}

		let videoEventHandler: (() => void) | null = null;

		fetchRewardProgramData(isAuthorized)
			.then((result) => {
				if (result.success && result.data) {
					setData({ rewardProgramData: result.data });
					// Auto-open popup on first visit if not seen before
					if (
						result.data.program?.active &&
						result.data.program?.templateMessage &&
						!appData.rewardsPopupSeen &&
						!disableAutoOpen
					) {
						const videoAlreadySeen =
							localStorage.getItem("automint_video_seen") === "true";
						const videoModalOpen =
							!!document.querySelector("[data-video-modal]");

						if (videoAlreadySeen && !videoModalOpen) {
							// Returning user, video not showing — open rewards immediately
							setIsPopupOpen(true);
							setData({ rewardsPopupSeen: true });
						} else {
							// First visit or video currently showing — wait for video close
							videoEventHandler = () => {
								setIsPopupOpen(true);
								setData({ rewardsPopupSeen: true });
							};
							window.addEventListener("video-dismissed", videoEventHandler, {
								once: true,
							});
						}
					}
				}
			})
			.finally(() => onReady?.());

		return () => {
			if (videoEventHandler) {
				window.removeEventListener("video-dismissed", videoEventHandler);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthorized]);

	// Don't show if no data yet, program not active, or no template message
	if (!rewardData?.program?.active || !rewardData?.program?.templateMessage)
		return null;

	const popup =
		isPopupOpen && typeof document !== "undefined"
			? createPortal(
					<div
						className="fixed inset-0 z-[9999] bg-black/20 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none flex items-center justify-center pointer-events-auto"
						onClick={() => setIsPopupOpen(false)}
					>
						<div
							className="z-[10000] md:fixed md:left-16 md:bottom-20 pointer-events-auto"
							onClick={(e) => e.stopPropagation()}
						>
							<ReferralCard
								onClose={() => setIsPopupOpen(false)}
								templateMessage={rewardData.program.templateMessage}
							/>
						</div>
					</div>,
					document.body,
				)
			: null;

	return (
		<>
			{/* Fixed Icon - right side on mobile (above callback), left side on desktop */}
			{!isPopupOpen && (
				<button
					onClick={() => setIsPopupOpen(true)}
					className={
						embedded
							? "pointer-events-auto hover:scale-105 transition-transform cursor-pointer"
							: "pointer-events-auto fixed right-4 md:right-auto md:left-16 bottom-[11.5rem] md:bottom-20 z-50 hover:scale-105 transition-transform cursor-pointer"
					}
					aria-label="Open rewards popup"
				>
					<Image
						src="/new_icons/referral/rewards-icon.svg"
						alt="Rewards"
						width={82}
						height={82}
						className="w-[58px] h-[58px] md:w-[82px] md:h-[82px]"
					/>
				</button>
			)}

			{/* Popup rendered in a portal so it's clickable even if a parent uses pointer-events: none */}
			{popup}
		</>
	);
}
